import { LONG_TEXT_MAX_LENGTH, TEXT_MAX_LENGTH } from '@servicetim/shared';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import type { FieldControlProps } from '../field-control';

const INPUT_TYPES = { text: 'text', url: 'url', email: 'email', phone: 'tel' } as const;
const PLACEHOLDERS = {
  text: undefined,
  url: 'https://',
  email: 'name@example.com',
  phone: '+15551234567',
} as const;

/** `text`, `url`, `email`, `phone`: a single-line input with the matching HTML type. */
export function TextControl(props: FieldControlProps) {
  const { field, value, onChange, onBlur, id, disabled, invalid, describedBy } = props;
  const type = field.type as keyof typeof INPUT_TYPES;
  const cfg = field.config;
  const maxLength = cfg?.type === 'text' ? cfg.maxLength : TEXT_MAX_LENGTH;
  return (
    <Input
      id={id}
      type={INPUT_TYPES[type] ?? 'text'}
      inputMode={type === 'phone' ? 'tel' : undefined}
      value={typeof value === 'string' ? value : ''}
      onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
      onBlur={onBlur}
      maxLength={maxLength}
      pattern={cfg?.type === 'text' && cfg.pattern ? cfg.pattern : undefined}
      placeholder={field.placeholder ?? PLACEHOLDERS[type]}
      readOnly={field.readOnly}
      disabled={disabled}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      aria-required={field.required || undefined}
      autoComplete="off"
    />
  );
}

/** `long_text`: textarea. */
export function LongTextControl(props: FieldControlProps) {
  const { field, value, onChange, onBlur, id, disabled, invalid, describedBy } = props;
  const cfg = field.config;
  return (
    <Textarea
      id={id}
      value={typeof value === 'string' ? value : ''}
      onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
      onBlur={onBlur}
      maxLength={cfg?.type === 'long_text' ? cfg.maxLength : LONG_TEXT_MAX_LENGTH}
      placeholder={field.placeholder}
      readOnly={field.readOnly}
      disabled={disabled}
      rows={field.key === 'description' ? 6 : 4}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      aria-required={field.required || undefined}
    />
  );
}

/** `number`: numeric input with min/max/step from the config and a unit suffix. */
export function NumberControl(props: FieldControlProps) {
  const { field, value, onChange, onBlur, id, disabled, invalid, describedBy } = props;
  const cfg = field.config?.type === 'number' ? field.config : undefined;
  const step = cfg ? 10 ** -cfg.precision : 'any';
  const input = (
    <Input
      id={id}
      type="number"
      inputMode="decimal"
      value={typeof value === 'number' && !Number.isNaN(value) ? String(value) : ''}
      onChange={(e) => {
        const raw = e.target.value;
        onChange(raw === '' ? undefined : Number(raw));
      }}
      onBlur={onBlur}
      min={cfg?.min ?? undefined}
      max={cfg?.max ?? undefined}
      step={step}
      placeholder={field.placeholder}
      readOnly={field.readOnly}
      disabled={disabled}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      aria-required={field.required || undefined}
      className={cfg?.unit ? 'pr-12' : undefined}
    />
  );
  if (!cfg?.unit) return input;
  return (
    <div className="relative">
      {input}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-fg-muted text-sm"
      >
        {cfg.unit}
      </span>
    </div>
  );
}
